import {
  AddressType,
  NotificationChannel,
  User,
  UserResponse,
  UserSettingsOptionsResponse,
} from 'types';
import { transformUserResponse } from './utils/transformUserResponse';
import { baseApi } from 'store/api/base-api';
import { RootState } from 'store/store';
import { ThemeId } from 'lib/hooks/useTheme';
import { setAuthenticated } from './auth-slice';

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    register: builder.mutation<
      unknown,
      {
        lastName: string;
        firstName: string;
        middleName: string;
        email: string;
        phone: string;
        password: string;
      }
    >({
      query: (userData) => ({
        url: 'register',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['User', 'Chat', 'Project'],
    }),

    login: builder.mutation<UserResponse, { email: string; password: string }>({
      query: (userData) => ({
        url: 'auth',
        method: 'POST',
        body: userData,
      }),

      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          const user = transformUserResponse(data.user);

          dispatch(setAuthenticated(true));

          dispatch(
            userApi.util.upsertQueryData('getUserData', undefined, user),
          );
        } catch {
          // console.log('Ошибка авторизации:', error);
          dispatch(setAuthenticated(false));
        }
      },
      invalidatesTags: ['User', 'Chat', 'Project'],
    }),

    logout: builder.mutation<void, void>({
      query: () => ({
        url: 'api/auth/logout',
        method: 'POST',
      }),
    }),

    sessionCheck: builder.query<void, void>({
      query: () => ({
        url: 'api/auth/session',
        method: 'GET',
      }),

      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(setAuthenticated(true));
        } catch {
          // 401 обрабатывается authErrorMiddleware
        }
      },
    }),

    getUserData: builder.query<User, void>({
      query: () => ({
        url: 'getUserData',
        method: 'GET',
      }),
      transformResponse: (response: UserResponse) => {
        const { user: userResponse } = response;

        const user = transformUserResponse(userResponse);
        return user;
      },
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(setAuthenticated(true));
        } catch {
          // 401 обрабатывается authErrorMiddleware
        }
      },
      providesTags: ['User'],
    }),

    changUserName: builder.mutation<
      User,
      Partial<Pick<User, 'firstName' | 'lastName' | 'middleName'>>
    >({
      async queryFn(userData, api, _extraOptions, baseQuery) {
        const state = api.getState() as RootState;
        const { data } = userApi.endpoints.getUserData.select()(state);

        const body = {
          firstName: data?.firstName,
          lastName: data?.lastName,
          middleName: data?.middleName,
          ...userData,
        };

        const response = await baseQuery({
          url: 'update-profile',
          method: 'POST',
          body,
        });

        if (response.error) return { error: response.error };

        const { user: userResponse } = response.data as UserResponse;
        const user = transformUserResponse(userResponse);

        api.dispatch(
          userApi.util.updateQueryData(
            'getUserData',
            undefined,
            (draft: User) => {
              Object.assign(draft, user);
            },
          ),
        );
        return { data: user };
      },
      // invalidatesTags: ['User'],
    }),

    resetPassword: builder.mutation<void, string>({
      query: (email) => ({
        url: 'reset-password',
        method: 'POST',
        body: { email },
      }),
    }),

    changePassword: builder.mutation<
      { success: true; message: string },
      { oldPassword: string; newPassword: string }
    >({
      query: (body) => ({ url: 'change-password', method: 'POST', body }),
    }),

    deleteAccount: builder.mutation<{ success: true; message: string }, void>({
      query: () => ({ url: 'delete-account', method: 'POST' }),
    }),

    getUserSettingsOptions: builder.query<UserSettingsOptionsResponse, void>({
      query: () => ({ url: 'api/account/settings/options', method: 'GET' }),
    }),

    updateUserProfile: builder.mutation<
      { success: boolean; message: string },
      { firstName: string; lastName: string; birthDate: string }
    >({
      async queryFn(body, api, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: '/api/account/profile',
          method: 'PATCH',
          body,
        });

        if (response.error) return { error: response.error };

        const data = response.data as { success: boolean; message: string };

        api.dispatch(
          userApi.util.updateQueryData(
            'getUserData',
            undefined,
            (draft: User) => {
              draft.firstName = body.firstName;
              draft.lastName = body.lastName;
              draft.birthDate = body.birthDate;
            },
          ),
        );

        return { data };
      },
    }),

    updateUserPersonalization: builder.mutation<
      { success: boolean; message: string },
      { addressName: string; addressType: AddressType }
    >({
      async queryFn(body, api, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: '/api/account/personalization',
          method: 'PATCH',
          body,
        });

        if (response.error) return { error: response.error };

        const data = response.data as { success: boolean; message: string };

        api.dispatch(
          userApi.util.updateQueryData(
            'getUserData',
            undefined,
            (draft: User) => {
              draft.personalization.addressName = body.addressName;
              draft.personalization.addressType = body.addressType;
            },
          ),
        );

        return { data };
      },
    }),

    updateUserInstruction: builder.mutation<
      { success: boolean; message: string },
      string
    >({
      async queryFn(instruction, api, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: '/api/account/personalization/instruction',
          method: 'PUT',
          body: { instruction },
        });

        if (response.error) return { error: response.error };

        const data = response.data as { success: boolean; message: string };

        api.dispatch(
          userApi.util.updateQueryData(
            'getUserData',
            undefined,
            (draft: User) => {
              draft.personalization.instruction = instruction;
            },
          ),
        );

        return { data };
      },
    }),

    deleteUserInstruction: builder.mutation<
      { success: boolean; message: string },
      void
    >({
      async queryFn(_, api, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: '/api/account/personalization/instruction',
          method: 'DELETE',
        });

        if (response.error) return { error: response.error };

        const data = response.data as { success: boolean; message: string };

        api.dispatch(
          userApi.util.updateQueryData(
            'getUserData',
            undefined,
            (draft: User) => {
              draft.personalization.instruction = '';
            },
          ),
        );

        return { data };
      },
    }),

    updateUserSettings: builder.mutation<
      { success: boolean; message: string },
      {
        notificationChannels: NotificationChannel[];
        newsAndUpdatesChannels: NotificationChannel[];
        theme: ThemeId;
      }
    >({
      async queryFn(body, api, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: '/api/account/settings',
          method: 'PATCH',
          body,
        });

        if (response.error) return { error: response.error };

        const data = response.data as { success: boolean; message: string };

        api.dispatch(
          userApi.util.updateQueryData(
            'getUserData',
            undefined,
            (draft: User) => {
              draft.settings.notificationChannels = body.notificationChannels;
              draft.settings.newsAndUpdatesChannels =
                body.newsAndUpdatesChannels;
              draft.settings.theme = body.theme;
            },
          ),
        );

        return { data };
      },
    }),

    reportProblem: builder.mutation<
      {
        success: boolean;
        emailSent: boolean;
        reportId: string;
        saved: boolean;
      },
      string
    >({
      async queryFn(message, api, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: '/api/account/problem-report',
          method: 'POST',
          body: { message },
        });

        if (response.error) return { error: response.error };

        const data = response.data as {
          success: boolean;
          emailSent: boolean;
          reportId: string;
          saved: boolean;
        };

        return { data };
      },
    }),

    changeUserEmail: builder.mutation<{ message: string }, string>({
      query: (newEmail) => ({
        url: '/change-mail',
        method: 'POST',
        body: { newEmail },
      }),
    }),
  }),
});

export const {
  useRegisterMutation,
  useLoginMutation,
  useLogoutMutation,
  useSessionCheckQuery,
  useGetUserDataQuery,
  useChangUserNameMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useDeleteAccountMutation,
  useGetUserSettingsOptionsQuery,
  useUpdateUserProfileMutation,
  useUpdateUserPersonalizationMutation,
  useUpdateUserInstructionMutation,
  useDeleteUserInstructionMutation,
  useUpdateUserSettingsMutation,
  useReportProblemMutation,
  useChangeUserEmailMutation,
} = userApi;
